<template>
  <div class="staffing-requirement-view">
    <div class="container my-4">
      <!-- Header with actions -->
      <div class="header-section">
        <h1>Staffing Requirement Details</h1>
        <div class="action-buttons">
          <router-link 
            v-if="$canAccess('StaffingRequirementEdit') && requirement"
            :to="{ name: 'StaffingRequirementEdit', params: { id: requirement.id }}" 
            class="btn btn-primary me-2"
          >
            <i class="fas fa-edit"></i> Edit
          </router-link>
          <button
            class="btn btn-secondary"
            @click="$router.push({ name: 'StaffingRequirementList' })"
          >
            <i class="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Details Card -->
      <div v-if="!loading && requirement" class="card mb-4">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ getDepartmentName(requirement.department_id) }} - {{ getShiftName(requirement.shift_id) }}
            </h5>
            <span 
              class="badge" 
              :class="requirement.is_active ? 'bg-success' : 'bg-danger'"
            >
              {{ requirement.is_active ? 'Active' : 'Inactive' }}
            </span>
          </div>
        </div>
        <div class="card-body">
          <div class="row mb-4">
            <div class="col-md-6">
              <h6 class="detail-label">Department</h6>
              <p class="detail-value">{{ getDepartmentName(requirement.department_id) }}</p>
            </div>
            <div class="col-md-6">
              <h6 class="detail-label">Shift</h6>
              <p class="detail-value">
                {{ getShiftName(requirement.shift_id) }}
                <span v-if="shift" class="text-muted">
                  ({{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }})
                </span>
              </p>
            </div>
          </div>
          
          <div class="row mb-4">
            <div class="col-md-4">
              <h6 class="detail-label">Minimum Nurses Required</h6>
              <p class="detail-value">{{ requirement.min_nurses_required }}</p>
            </div>
            <div class="col-md-4">
              <h6 class="detail-label">Minimum Charge Nurses Required</h6>
              <p class="detail-value">{{ requirement.min_charge_nurses_required }}</p>
            </div>
            <div class="col-md-4">
              <h6 class="detail-label">Minimum Team Leads Required</h6>
              <p class="detail-value">{{ requirement.min_team_leads_required }}</p>
            </div>
          </div>
          
          <div class="row mb-4">
            <div class="col-12">
              <h6 class="detail-label">Required Specializations</h6>
              <div class="detail-value">
                <div v-if="requirement.required_specializations && requirement.required_specializations.length > 0">
                  <span 
                    v-for="specId in requirement.required_specializations" 
                    :key="specId"
                    class="badge bg-info me-2 mb-2"
                  >
                    {{ getSpecializationName(specId) }}
                  </span>
                </div>
                <p v-else class="text-muted">No specific specializations required</p>
              </div>
            </div>
          </div>
          
          <div class="row">
            <div class="col-12">
              <h6 class="detail-label">Notes</h6>
              <p class="detail-value">
                {{ requirement.notes || 'No notes available' }}
              </p>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <div class="d-flex justify-content-between align-items-center">
            <small class="text-muted">Last updated: {{ $formatDateTime(requirement.updated_at) }}</small>
            <button
              v-if="$canAccess('StaffingRequirementEdit')"
              class="btn btn-sm"
              :class="requirement.is_active ? 'btn-outline-danger' : 'btn-outline-success'"
              @click="toggleStatus"
            >
              {{ requirement.is_active ? 'Deactivate' : 'Activate' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StaffingRequirementView',
  props: {
    id: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      requirement: null,
      departments: [],
      shifts: [],
      specializations: [],
      loading: false,
      error: null
    }
  },
  computed: {
    shift() {
      if (!this.requirement || !this.requirement.shift_id) return null;
      return this.shifts.find(s => s.id === this.requirement.shift_id);
    }
  },
  methods: {
    formatTime(timeString) {
      if (!timeString) return '';
      
      try {
        const date = new Date(`2000-01-01T${timeString}`);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return timeString;
      }
    },
    
    async loadRequirement() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get(`/staffing-requirements/${this.id}`);
        this.requirement = response.data.data;
      } catch (error) {
        console.error('Error loading staffing_requirements requirement:', error);
        this.error = error.response?.data?.message || 'Failed to load staffing_requirements requirement';
      } finally {
        this.loading = false;
      }
    },
    
    async loadLookupData() {
      try {
        // Load departments
        const departmentsResponse = await this.$api.get('/departments');
        this.departments = departmentsResponse.data.data;

        // Load shifts
        const shiftsResponse = await this.$api.get('/shifts');
        this.shifts = shiftsResponse.data.data;

        // Load specializations
        const specializationsResponse = await this.$api.get('/specializations');
        this.specializations = specializationsResponse.data.data;
      } catch (error) {
        console.error('Error loading lookup data:', error);
        this.error = 'Failed to load required data';
      }
    },
    
    getDepartmentName(departmentId) {
      const department = this.departments.find(d => d.id === departmentId);
      return department ? department.name : 'Unknown';
    },

    getShiftName(shiftId) {
      const shift = this.shifts.find(s => s.id === shiftId);
      return shift ? shift.name : 'Unknown';
    },

    getSpecializationName(specializationId) {
      const specialization = this.specializations.find(s => s.id === specializationId);
      return specialization ? specialization.name : 'Unknown';
    },
    
    // async toggleStatus() {
    //   try {
    //     const action = this.requirement.is_active ? 'deactivate' : 'activate';
    //     if (!confirm(`Are you sure you want to ${action} this staffing requirement?`)) {
    //       return;
    //     }
    //
    //     await this.$api.post(`/staffing-requirements/${this.id}/toggle-status`);
    //     this.$toast.success(`Staffing requirement ${action}d successfully`);
    //     this.loadRequirement();
    //   } catch (error) {
    //     console.error('Error toggling status:', error);
    //     this.$toast.error(error.response?.data?.message || `Failed to ${action} staffing requirement`);
    //   }
    // }
  },
  mounted() {
    this.loadLookupData();
    this.loadRequirement();
  }
}
</script>

<style scoped>
.staffing-requirement-view {
  padding-bottom: 2rem;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.detail-label {
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.detail-value {
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .action-buttons {
    width: 100%;
  }
}
</style>
